import React, { useState, useEffect, useCallback } from "react"
import request from "request"
import Flash from "flash"

import Header from "Header"
import AsyncContainer from "AsyncContainer"
import Button from "Button"
import Table from "Table"
import Paginate from "Paginate"
import { StatusTag, EndpointStatusTag, formatTime, copyText } from "../WebhooksList/webhookHelpers"

const STATUS_FILTERS = {
  "": "All statuses",
  pending: "Pending",
  delivered: "Delivered",
  failed: "Failed (retrying)",
  exhausted: "Exhausted (gave up)",
}

const DeliveryDetails = ({ delivery }) => (
  <div className="box">
    <div className="columns">
      <div className="column">
        <p className="has-text-weight-bold">Request payload</p>
        <pre className="is-size-7" style={{ maxHeight: 400, overflow: "auto" }}>
          {JSON.stringify(delivery.payload, null, 2)}
        </pre>
      </div>
      <div className="column">
        <p className="has-text-weight-bold">
          Response {delivery.responseStatus ? `HTTP ${delivery.responseStatus}` : ""}
        </p>
        {delivery.errorMessage && <p className="has-text-danger">{delivery.errorMessage}</p>}
        <pre className="is-size-7" style={{ maxHeight: 400, overflow: "auto" }}>
          {delivery.responseBody || "(empty body)"}
        </pre>
        <p className="help">
          Event id <code>{delivery.eventId}</code> · Delivery id <code>{delivery.id}</code>
        </p>
      </div>
    </div>
  </div>
)

const WebhookEndpointView = ({ computedMatch }) => {
  const endpointId = computedMatch.params.id
  const [endpoint, setEndpoint] = useState(null)
  const [revealedSecret, setRevealedSecret] = useState(null)
  const [deliveries, setDeliveries] = useState([])
  const [pagy, setPagy] = useState({})
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState("")
  const [expanded, setExpanded] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const loadEndpoint = useCallback(
    () => request.get(`/venue_admin/webhook_endpoints/${endpointId}`).then(({ data }) => setEndpoint(data.webhookEndpoint)),
    [endpointId]
  )

  const loadDeliveries = useCallback(
    () =>
      request
        .get(`/venue_admin/webhook_endpoints/${endpointId}/deliveries`, { params: { page, status: status || undefined } })
        .then(({ data }) => {
          setDeliveries(data.pageData || [])
          setPagy(data.pagy || {})
          setIsLoaded(true)
        }),
    [endpointId, page, status]
  )

  useEffect(() => { loadEndpoint() }, [loadEndpoint])
  useEffect(() => { loadDeliveries() }, [loadDeliveries])

  const handleTest = () => {
    request.post(`/venue_admin/webhook_endpoints/${endpointId}/test`).then(() => {
      Flash.broadcast("success", "Test event queued. Refresh in a moment to see the delivery.")
      setTimeout(loadDeliveries, 1500)
    })
  }

  const handleRotate = () => {
    if (!confirm("Rotate the signing secret? Deliveries signed with the old secret will fail verification on your side until you update it.")) return
    request.post(`/venue_admin/webhook_endpoints/${endpointId}/rotate_secret`).then(({ data }) => {
      setEndpoint(data.webhookEndpoint)
      setRevealedSecret(data.webhookEndpoint.secret)
    })
  }

  const handleRedeliver = (delivery) => {
    request
      .post(`/venue_admin/webhook_endpoints/${endpointId}/deliveries/${delivery.id}/redeliver`)
      .then(() => {
        Flash.broadcast("success", "Delivery queued again.")
        setTimeout(loadDeliveries, 1500)
      })
  }

  const HEADERS = {
    createdAt: "Created",
    eventType: "Event",
    status: "Status",
    attempts: "Attempts",
    responseStatus: "Response",
    lastAttemptedAt: "Last Attempt",
    actions: "Actions",
  }

  const FORMATTERS = {
    createdAt: (d) => formatTime(d.createdAt),
    eventType: (d) => <code>{d.eventType}</code>,
    status: (d) => <StatusTag status={d.status} />,
    attempts: (d) => String(d.attempts),
    responseStatus: (d) => (d.responseStatus ? `HTTP ${d.responseStatus}` : d.errorMessage || "-"),
    lastAttemptedAt: (d) => formatTime(d.lastAttemptedAt),
    actions: (d) => (
      <div className="buttons are-small">
        <Button
          text={expanded === d.id ? "Hide" : "Details"}
          size="small"
          onClick={() => setExpanded(expanded === d.id ? null : d.id)}
        />
        <Button text="Resend" size="small" onClick={() => handleRedeliver(d)} />
      </div>
    ),
  }

  const expandedDelivery = deliveries.find((d) => d.id === expanded)

  return (
    <div>
      <Header text={endpoint ? endpoint.name : "Webhook Endpoint"} backLink="/webhooks" />

      <AsyncContainer isLoaded={!!endpoint}>
        {endpoint && (
          <div className="box">
            <div className="columns">
              <div className="column">
                <p className="has-text-weight-bold">URL</p>
                <p><code>{endpoint.url}</code></p>
                <p className="has-text-weight-bold mt-3">Events</p>
                <p>{endpoint.events.map((t) => <span key={t} className="tag is-light mr-1">{t}</span>)}</p>
                <p className="has-text-weight-bold mt-3">Status</p>
                <p><EndpointStatusTag endpoint={endpoint} /></p>
                {endpoint.disabledReason && <p className="help has-text-danger">{endpoint.disabledReason}</p>}
              </div>
              <div className="column">
                <p className="has-text-weight-bold">Signing secret</p>
                <div className="field has-addons">
                  <div className="control is-expanded">
                    <input className="input is-family-monospace is-size-7" readOnly value={revealedSecret || endpoint.secret} />
                  </div>
                  {revealedSecret && (
                    <div className="control">
                      <Button text="Copy" onClick={() => copyText(revealedSecret, "Signing secret copied")} />
                    </div>
                  )}
                </div>
                {revealedSecret ? (
                  <p className="help has-text-danger">This is the only time the new secret is shown. Copy it now.</p>
                ) : (
                  <p className="help">The secret is shown once at creation. Rotate it if it was lost.</p>
                )}
                <p className="has-text-weight-bold mt-3">Last delivered</p>
                <p>{formatTime(endpoint.lastDeliveredAt)}</p>
                <p className="has-text-weight-bold mt-3">Last failure</p>
                <p>{formatTime(endpoint.lastFailedAt)}</p>
              </div>
            </div>
            <div className="buttons">
              <Button text="Send test event" color="primary" onClick={handleTest} />
              <Button text="Rotate secret" onClick={handleRotate} />
              <Button text="Refresh deliveries" onClick={loadDeliveries} />
            </div>
          </div>
        )}
      </AsyncContainer>

      <h2 className="title is-5">Deliveries</h2>
      <div className="field">
        <div className="control">
          <div className="select">
            <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }}>
              {Object.entries(STATUS_FILTERS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <AsyncContainer isLoaded={isLoaded}>
        {deliveries.length === 0 ? (
          <div className="notification is-info">
            No deliveries yet. Send a test event, or wait for a subscribed event to happen in this venue.
          </div>
        ) : (
          <>
            <Table rows={deliveries} headers={HEADERS} formatters={FORMATTERS} />
            {expandedDelivery && <DeliveryDetails delivery={expandedDelivery} />}
            <Paginate {...pagy} changePage={setPage} />
          </>
        )}
      </AsyncContainer>
    </div>
  )
}

export default WebhookEndpointView
